import { createSelector } from 'reselect';
import { getUsers, getAllUsers, getClientProfiles, getUserOnlineArray } from 'selectors/entities/userSelectors';
import { getTaskLabels, getTaskStatuses, getTasksWithSummary } from 'selectors/entities/taskSelectors';
import { getProjects } from 'selectors/entities/projectsSelectors';
import { List, Map } from 'immutable';
import { sortDate } from 'helpers/utilHelpers';
import { BATCH_UPDATE_TYPE, MONITORING_TYPE } from 'constants/NotificationConstants';

// Returns visibility of the notifications drawer
export const getNotificationDrawerVisibility = (state) => state.getIn(['ui', 'notificationDrawer', 'visible']);

// Returns loading state of the notifications drawer
export const getNotificationDrawerLoading = (state) => state.getIn(['ui', 'notificationDrawer', 'loading']);

// Returns notifications of the notifications drawer
export const getNotificationDrawerNotifications = (state) => state.getIn(['ui', 'notificationDrawer', 'notifications']);

export const getNotificationDrawerNotificationsDataset = (state) =>
  state.getIn(['ui', 'notificationDrawer', 'notificationDataset']);

// Returns task view state of the notification drawer
export const getNotificationDrawerTaskView = (state) => state.getIn(['ui', 'notificationDrawer', 'taskView']);

// Returns not viewed notifications of the notifications drawer
export const getNotificationDrawerNotificationsViewed = (state) =>
  state.getIn(['ui', 'notificationDrawer', 'notifications']).filter((notification) => !notification.get('viewed'));

// Return total page count
export const getNotificationPagination = (state) => state.getIn(['ui', 'notificationDrawer', 'pagination']);

// Return notifications filters
export const getNotificationFilters = (state) => state.getIn(['settings', 'userState', 'notificationPanel', 'filters']);

export const getDrawerTaskListDataset = (state) => state.getIn(['ui', 'notificationDrawer', 'taskList', 'dataset']);

export const getDrawerTaskListPagination = (state) =>
  state.getIn(['ui', 'notificationDrawer', 'taskList', 'pagination']);

// Returns count of the notifications
export const getNewNotificationsCount = (state) => {
  const newNotifications = state
    .getIn(['ui', 'notificationDrawer', 'notifications'])
    .filter((notification) => !notification.get('viewed'));

  if (newNotifications) {
    return newNotifications.size;
  }

  return 0;
};

const getFiltredNotifications = createSelector(
  [getNotificationDrawerNotifications, getNotificationDrawerNotificationsDataset, getNotificationFilters],
  (notifications, dataset, filters) => {
    return dataset
      .map((feedId) => notifications.get(feedId))
      .filter((notifaction) => {
        const selectedKey = filters?.keySeq().first();
        switch (selectedKey) {
          case 'monitoring':
          case 'mention':
            return notifaction?.get('type') === selectedKey + '-type';
          case 'comment':
            return notifaction?.get('entity') === 'Comment';
          case 'project':
            return notifaction?.get('entity') === 'Project' && notifaction?.get('type') !== MONITORING_TYPE;
          case 'task':
            return notifaction?.get('entity') === 'Task';
          case 'new':
            return notifaction?.get('viewed') === 0;
          default:
            return true;
        }
      });
  },
);

const getNotificationWithModifiedValues = createSelector(
  [getFiltredNotifications, getAllUsers, getTaskLabels, getTaskStatuses, getClientProfiles],
  (notifications, users, labels, statuses, clientProfiles) => {
    return notifications.map((notification) => {
      if (!notification) {
        return null;
      }
      if (notification.getIn(['relatedEntity', 'type']) === 'task') {
        let model = notification.getIn(['relatedEntity', 'model']);
        let user = users.get(model.get('userId'));
        if (!user) {
          user = clientProfiles.get(model.get('userId'));
        }
        model = model.set('user', user);
        notification = notification.setIn(['relatedEntity', 'model'], model);
      }

      if (notification.getIn(['content', 'modifiedValues'])) {
        let modifiedValues = notification.getIn(['content', 'modifiedValues']);

        if (modifiedValues.getIn(['newValues', 'userId']) || modifiedValues.getIn(['oldValues', 'userId'])) {
          modifiedValues = modifiedValues.setIn(
            ['newValues', 'user'],
            users.get(modifiedValues.getIn(['newValues', 'userId'])) ||
              clientProfiles.get(modifiedValues.getIn(['newValues', 'userId'])),
          );

          modifiedValues = modifiedValues.setIn(
            ['oldValues', 'user'],
            users.get(modifiedValues.getIn(['oldValues', 'userId'])) ||
              clientProfiles.get(modifiedValues.getIn(['oldValues', 'userId'])),
          );
        }

        if (
          modifiedValues.getIn(['newValues', 'coPerformerId']) ||
          modifiedValues.getIn(['oldValues', 'coPerformerId'])
        ) {
          modifiedValues = modifiedValues.setIn(
            ['newValues', 'coPerformer'],
            users.get(modifiedValues.getIn(['newValues', 'coPerformerId'])) ||
              clientProfiles.get(modifiedValues.getIn(['newValues', 'coPerformerId'])),
          );

          modifiedValues = modifiedValues.setIn(
            ['oldValues', 'coPerformer'],
            users.get(modifiedValues.getIn(['oldValues', 'coPerformerId'])) ||
              clientProfiles.get(modifiedValues.getIn(['oldValues', 'coPerformerId'])),
          );
        }

        if (modifiedValues.getIn(['newValues', 'labelIds'])) {
          modifiedValues = modifiedValues.setIn(
            ['newValues', 'labels'],
            modifiedValues.getIn(['newValues', 'labelIds']).map((id) => labels.get(id)),
          );

          modifiedValues = modifiedValues.setIn(
            ['oldValues', 'labels'],
            modifiedValues.getIn(['oldValues', 'labelIds']).map((id) => labels.get(id)),
          );
        }

        if (modifiedValues.getIn(['newValues', 'statusId'])) {
          modifiedValues = modifiedValues.setIn(
            ['newValues', 'taskStatus'],
            statuses.get(modifiedValues.getIn(['newValues', 'statusId'])),
          );

          modifiedValues = modifiedValues.setIn(
            ['oldValues', 'taskStatus'],
            statuses.get(modifiedValues.getIn(['oldValues', 'statusId'])),
          );
        }

        notification = notification.setIn(['content', 'modifiedValues'], modifiedValues);
      }

      // Prepare data for batch update type
      if (notification.get('type') === BATCH_UPDATE_TYPE) {
        let modifiedValues = notification.getIn(['content', 'modifiedValues']);
        if (modifiedValues?.get('action') === 'assign') {
          const user = users.get(modifiedValues.getIn(['value', 'userId']));
          const client = clientProfiles.get(modifiedValues.getIn(['value', 'userId']));
          modifiedValues = modifiedValues.setIn(['value', 'user'], user || client);
        }
        if (modifiedValues?.get('action') === 'labels') {
          if (modifiedValues.getIn(['value', 'statusId'])) {
            modifiedValues = modifiedValues.setIn(
              ['value', 'status'],
              statuses.get(modifiedValues.getIn(['value', 'statusId'])),
            );
          }
          if (modifiedValues.getIn(['value', 'labelsIds'])?.size) {
            modifiedValues = modifiedValues.setIn(
              ['value', 'labels'],
              modifiedValues
                .getIn(['value', 'labelsIds'])
                .map((item) => labels.get(item))
                .filter((item) => item),
            );
          }
        }

        notification = notification.setIn(['content', 'modifiedValues'], modifiedValues);
      }

      return notification;
    });
  },
);

// Returns notifications grouped by date
export const getNotificationsSummary = createSelector(
  [getNotificationWithModifiedValues, getUsers, getClientProfiles, getProjects, getUserOnlineArray],
  (notifications, users, clientProfiles, projects, online) => {
    let byDate = new Map();

    if (notifications && notifications.size) {
      notifications
        .sort((first, second) => {
          return -sortDate(first?.get('timeCreated'), second?.get('timeCreated'), 'YYYY-MM-DD HH:mm:ss');
        })
        .forEach((notification) => {
          if (notification) {
            const date = notification.get('timeCreated', '').split(' ')[0];
            let user = users.find((user) => user.get('userId') === notification.getIn(['content', 'userId']));
            if (!user) {
              user = clientProfiles.get(notification.getIn(['content', 'userId']));
            }
            let list = byDate.get(date);
            if (!list) {
              list = new List();
            }

            let notificationSymmary = notification.set('project', projects?.get(notification.get('projectId')));
            if (user && notificationSymmary.get('sender')) {
              notificationSymmary = notificationSymmary
                .set('user', user)
                .setIn(['sender', 'online'], online?.has(notificationSymmary.getIn(['sender', 'userId'])) || false);
            }
            byDate = byDate.set(date, list.push(notificationSymmary));
          }
        });
    }

    return byDate;
  },
);

export const getNotificationsByProjects = createSelector(
  [getNotificationWithModifiedValues, getUsers, getClientProfiles, getUserOnlineArray],
  (notifications, users, clientProfiles, online) => {
    let byProject = new Map();
    if (notifications && notifications.size) {
      notifications
        .sort((first, second) => {
          return -sortDate(first.get('timeCreated'), second.get('timeCreated'), 'YYYY-MM-DD HH:mm:ss');
        })
        .forEach((notification) => {
          const projectName = notification.getIn(['relatedEntity', 'model', 'project', 'name']);
          let user = users.find((user) => user.get('userId') === notification.getIn(['content', 'userId']));
          if (!user) {
            user = clientProfiles.get(notification.getIn(['content', 'userId']));
          }
          let list = byProject.get(projectName);
          if (!list) {
            list = new List();
          }
          if (projectName) {
            byProject = byProject.set(
              projectName,
              list.push(
                notification
                  .set('user', user)
                  .setIn(['sender', 'online'], online?.has(notification.getIn(['sender', 'userId'])) || false),
              ),
            );
          }
        });
    }
    return byProject;
  },
);

export const getDrawerTasksByDataset = createSelector(
  [getDrawerTaskListDataset, getTasksWithSummary],
  (dataset, tasks) => {
    return dataset.map((taskId) => tasks.get(taskId));
  },
);
